import { ApiStatusToFileStatus, PRIORITIZE_PAGE_CONTENT } from "@utils/constants";
import { getOptionValueList, matchOrCreateItems } from "@utils/helpers";
import { nonNullable } from "@utils/utils";
import React, { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";

import { MultiSelectItem } from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerFileFileKindChoices,
  ApiLearnerFileStatusChoices,
  ApiReportSectionsAiGeneratedStatusChoices,
  ApiReportTemplateFileUploadConfigChoices,
  AssessmentToolType,
  ReportPageLocationEnums,
  ReportTypeEnums,
  useAddUpdateReportTreatmentPlanPrioritizeFieldsMutation,
  useAssessmentToolsQuery,
} from "@api/graphql/types-and-hooks";
import { InlineMultiSelectField } from "@components/BlurBasedFormFields";
import { useBlurBasedForm } from "@components/BlurBasedFormFields/useBlurBasedForm";
import { ConfirmGenerationDialog } from "@components/ConfirmGenerationDialog/ConfirmGenerationDialogV2";
import { ContentRow } from "@components/ContentRow/ContentRow";
import { EnhanceAccuracyDialog, getAccuracyContentList } from "@components/EnhanceAccuracyDialog/EnhanceAccuracyDialog";
import { REASONS_TO_BE_OPEN, ValidationNoticeDialog } from "@components/ValidationNoticeDialog/ValidationNoticeDialog";
import { notifyError } from "@components/notifications/notifications";
import { FooterButtonRow } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/FooterButtonRow";
import { Assessments } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/UploadFiles/Assessments/Assessments";
import { BHTIAFiles } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/UploadFiles/BHTIAFiles";
import { BehavioralObservations } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/UploadFiles/BehavioralObservations.tsx";
import { ClinicalNotes } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/UploadFiles/ClinicalNotes";
import { DiagnosisReport } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/UploadFiles/DiagnosisReport";
import { IntakeCalls } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/UploadFiles/IntakeCall";
import {
  FilterSectionSubRoute,
  useGetFilteredPageSectionsByLocation,
} from "@pages/AssessmentReportV2Details/Components/helper";
import { useAdminData } from "@providers/AdminDataProvider";
import { useReport } from "@providers/ReportProvider";

type UploadFilesPrioritizeState = {
  safetyConcerns: MultiSelectItem[];
};

export const LegacyUploadFiles: React.FC = () => {
  const { report, reportQuery } = useReport();

  const { assessmentId: reportId } = useParams();

  const go = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  /** Only allow click-outside autosave after we've hydrated from server; avoids overwriting server data on initial load */
  const hasHydratedRef = useRef<boolean>(false);

  const files = report.reportFiles.edges?.map((edge) => edge?.node);
  const stillParsing = files?.some(
    (f) => f?.status == ApiLearnerFileStatusChoices.Processing || f?.status == ApiLearnerFileStatusChoices.Pending,
  );
  const { refetch } = reportQuery;
  const response = useAssessmentToolsQuery({ reportType: ReportTypeEnums.InitialAssessment });
  const supportedAssessmentsList: AssessmentToolType[] =
    response.data?.assessmentTools?.filter((item) => !!item).filter((tool) => tool.parsingSupported) ?? [];

  const assessmentIds = new Set(supportedAssessmentsList.map((a) => a.id));

  const allMappedFiles =
    files?.filter(nonNullable).map((file) => ({
      fileName: file.originalFileName || "<missing-file-name>",
      fileSize: file.fileSize,
      fileStatus: ApiStatusToFileStatus[file.status || ApiLearnerFileStatusChoices.Pending],
      metadata: file.metadata,
      fileKind: file.fileKind || "<missing-file-kind>",
      fileId: file.id,
      assessmentTemplateType: file.assessmentTemplateType,
      failureReason: file.processingFailureReason ?? "",
      failureCode: file.processingFailureCode,
    })) ?? [];

  const existingIntakeCalls = allMappedFiles.filter((f) => f.fileKind === ApiLearnerFileFileKindChoices.IntakeCall);
  const existingDiagnosis = allMappedFiles.find((f) => f.fileKind === ApiLearnerFileFileKindChoices.DiagnosisReport);
  const existingAssessmentFiles = allMappedFiles.filter((f) => assessmentIds.has(f.fileKind));
  const existingBHTIAFiles = allMappedFiles.filter((f) => f.fileKind === ApiLearnerFileFileKindChoices.Bhtia);
  const existingBehavioralObservations = allMappedFiles.filter(
    (f) => f.fileKind === ApiLearnerFileFileKindChoices.BehavioralObservations,
  );
  const existingClinicalNotes = allMappedFiles.filter(
    (f) => f.fileKind === ApiLearnerFileFileKindChoices.ClinicalNotes,
  );

  const [promptDialogOpen, setPromptDialogOpen] = React.useState<boolean>(false);
  const [validationDialogOpen, setValidationDialogOpen] = React.useState<REASONS_TO_BE_OPEN | null>(null);
  const [accuracyDialogOpen, setAccuracyDialogOpen] = React.useState<boolean>(false);
  const [accuracyDialogContent, setAccuracyDialogContent] = React.useState<string[]>([]);
  const [selectedSafetyConcern, setSelectedSafetyConcern] = React.useState<MultiSelectItem[] | []>([]);
  const [showAccuracyContentWarning] = useLocalStorage("show-accuracy-content-warning", true);

  const nextPage: FilterSectionSubRoute = "report-part-1";
  const nextPageFilteredSections = useGetFilteredPageSectionsByLocation(nextPage);
  const addUpdatePrioritizeMutation = useAddUpdateReportTreatmentPlanPrioritizeFieldsMutation({});

  const nextPageAlreadyGenerated = nextPageFilteredSections.some(
    (section) =>
      section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending ||
      section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPredicted,
  );

  const { safetyConcern } = PRIORITIZE_PAGE_CONTENT;
  const reportTreatmentPlanPrioritizeFields = report.reportTreatmentPlanPrioritizeFields;
  const isCatalightTemplate =
    report.reportTemplate?.fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight.valueOf();
  useEffect(() => {
    if (!reportTreatmentPlanPrioritizeFields) return;
    if (reportTreatmentPlanPrioritizeFields.safetyConcerns) {
      setSelectedSafetyConcern(
        matchOrCreateItems(
          reportTreatmentPlanPrioritizeFields.safetyConcerns as string[],
          safetyConcern.optionItems as MultiSelectItem[],
        ),
      );
    }
    hasHydratedRef.current = true;
  }, [reportTreatmentPlanPrioritizeFields, safetyConcern.optionItems]);

  const savePrioritize = useCallback(
    async (overrides?: Partial<UploadFilesPrioritizeState>) => {
      const safetyConcerns = overrides?.safetyConcerns ?? selectedSafetyConcern;
      try {
        await addUpdatePrioritizeMutation.mutateAsync({
          reportId: reportId ? reportId : "missing-report-id",
          prioritizeFieldsDetails: {
            familyLife: reportTreatmentPlanPrioritizeFields?.familyLife ?? [],
            communicationAbility: reportTreatmentPlanPrioritizeFields?.communicationAbility ?? [],
            engagementAbility: reportTreatmentPlanPrioritizeFields?.engagementAbility ?? false,
            socialDesires: reportTreatmentPlanPrioritizeFields?.socialDesires,
            safetyConcerns: safetyConcerns.length ? getOptionValueList(safetyConcerns) : [],
          },
        });
      } catch (error) {
        console.error("Error Saving Challenging Behaviors", error);
        notifyError("Error Saving Challenging Behaviors");
        throw error;
      }
    },
    [selectedSafetyConcern, reportId, reportTreatmentPlanPrioritizeFields, addUpdatePrioritizeMutation],
  );

  const setters = { safetyConcerns: setSelectedSafetyConcern };
  const normalizeValue = <K extends keyof UploadFilesPrioritizeState>(
    key: K,
    value: unknown,
  ): UploadFilesPrioritizeState[K] => {
    if (key === "safetyConcerns") {
      return (Array.isArray(value) ? value : []) as UploadFilesPrioritizeState[K];
    }
    return value as UploadFilesPrioritizeState[K];
  };

  const state: UploadFilesPrioritizeState = { safetyConcerns: selectedSafetyConcern };

  const { handleFieldSave } = useBlurBasedForm<UploadFilesPrioritizeState>({
    containerRef,
    mutationFn: savePrioritize,
    isSaveReady: () => hasHydratedRef.current,
    state,
    setters,
    normalizeValue,
  });

  const { isAcesUser, isTestAcesUser } = useAdminData();

  const handleNext = async () => {
    await savePrioritize();
    if (nextPageAlreadyGenerated) {
      go(`../${nextPage}`);
      return;
    }
    try {
      const updatedFiles = (await refetch()).data?.getReports?.edges?.[0]?.node?.reportFiles.edges?.map(
        (edge) => edge?.node,
      );

      const filesForAssessment = updatedFiles?.filter((file) => file?.status !== "PENDING")?.filter(nonNullable);

      const existingAssessments = filesForAssessment?.filter((file) =>
        supportedAssessmentsList.map((a) => a.id).includes(file.fileKind),
      );

      const parsingFiles = existingAssessments?.filter(
        (file) => file.status === ApiLearnerFileStatusChoices.Processing,
      );

      const errorFiles = existingAssessments?.filter(
        (file) => file.status === ApiLearnerFileStatusChoices.ProcessError,
      );

      if (existingAssessments?.length === 0) {
        setValidationDialogOpen("MISSING_FILES");
        return;
      }

      if (parsingFiles && parsingFiles.length > 0) {
        setValidationDialogOpen("FILES_VALIDATING");
        return;
      }

      if (errorFiles && errorFiles.length > 0) {
        setValidationDialogOpen("PARSING_FAILED");
        return;
      }

      const tempAccuracyContentList = getAccuracyContentList({
        existingAssessments: existingAssessments?.length === 0,
        existingDiagnosis: !existingDiagnosis,
        existingBHTIAFiles: isCatalightTemplate && existingBHTIAFiles?.length === 0,
        existingIntakeCalls: existingIntakeCalls?.length === 0,
        existingChallengingBehavior: selectedSafetyConcern?.length === 0,
      });

      if (tempAccuracyContentList.length > 0 && showAccuracyContentWarning) {
        setAccuracyDialogContent(tempAccuracyContentList);
        setAccuracyDialogOpen(true);
        return;
      } else {
        setPromptDialogOpen(true);
      }
    } catch (error) {
      console.error("Assessment file retrieval error: ", error);
      notifyError("Assessment file retrieval error");
    }
  };

  // Special cases require custom order and overriding titles/subtitles
  // in future template this out and store in db
  const renderAcesUploadSections = () => (
    <>
      <IntakeCalls
        existingIntakeCalls={existingIntakeCalls}
        title="Interview Recording"
        subtitle="Upload any recordings of your interview with caregivers"
      />
      <BehavioralObservations
        existingBehavioralObservations={existingBehavioralObservations}
        title="Interview & Observation Notes"
        subtitle="Upload standardized notes template"
      />
      <BHTIAFiles
        existingBHTIAFiles={existingBHTIAFiles}
        title="ACES Intake Form or BHT"
        subtitle="Upload the standardized ACES Intake Form or the BHTIA/BHTSF (for CCS clients only)."
      />
      <DiagnosisReport title="Diagnostic Report" existingDiagnosis={existingDiagnosis} />
      <Assessments existingAssessments={existingAssessmentFiles} />
    </>
  );

  const renderCatalightUploadSections = () => (
    <>
      <IntakeCalls existingIntakeCalls={existingIntakeCalls} />
      <DiagnosisReport existingDiagnosis={existingDiagnosis} />
      <BHTIAFiles existingBHTIAFiles={existingBHTIAFiles} />
      <BehavioralObservations
        existingBehavioralObservations={existingBehavioralObservations}
        title="Clinical Notes"
        subtitle="Upload standardized notes template"
      />
      <Assessments existingAssessments={existingAssessmentFiles} />
    </>
  );

  const renderFileUploadSections = () => {
    if (isAcesUser || isTestAcesUser) {
      return renderAcesUploadSections();
    }
    if (isCatalightTemplate) {
      return renderCatalightUploadSections();
    }

    return (
      <>
        <IntakeCalls existingIntakeCalls={existingIntakeCalls} />
        <DiagnosisReport existingDiagnosis={existingDiagnosis} />
        <BehavioralObservations existingBehavioralObservations={existingBehavioralObservations} />
        <Assessments existingAssessments={existingAssessmentFiles} />
        <ClinicalNotes existingClinicalNotes={existingClinicalNotes} />
      </>
    );
  };

  return (
    <div className="flex w-full flex-col gap-y-4 pb-10 pt-6" ref={containerRef}>
      <ValidationNoticeDialog reasonToBeOpen={validationDialogOpen} closeDialog={() => setValidationDialogOpen(null)} />
      <ConfirmGenerationDialog
        type={ReportPageLocationEnums.ReportPart_1}
        promptDialogOpen={promptDialogOpen}
        setPromptDialogOpen={setPromptDialogOpen}
      />
      <EnhanceAccuracyDialog
        accuracyDialogContent={accuracyDialogContent}
        accuracyDialogOpen={accuracyDialogOpen}
        setAccuracyDialogOpen={setAccuracyDialogOpen}
        onContinue={() => setPromptDialogOpen(true)}
      />
      {renderFileUploadSections()}
      <ContentRow
        title="Challenging Behaviors"
        subtitle="Let us know about any behaviors you're tracking that may need an FBA. Choose from our list of common behaviors or add your own."
      >
        <InlineMultiSelectField<UploadFilesPrioritizeState>
          value={selectedSafetyConcern}
          fieldKey="safetyConcerns"
          onSave={handleFieldSave}
          title="Challenging Behaviors"
          multiSelectItems={safetyConcern.optionItems as MultiSelectItem[]}
          multiSelectMode="tags"
        />
      </ContentRow>
      <FooterButtonRow
        primaryButtonDisabled={reportQuery.isFetching || stillParsing}
        primaryButtonAction={handleNext}
        skipNavigate
      />
    </div>
  );
};
